import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import { createSelector } from "reselect";
import { getAvatarStyle } from "utils/utils";

const ProfileDropdown = () => {
  // Memoized selector to look up the local profile node slice state cleanly
  const profiledropdownData = useMemo(
    () =>
      createSelector(
        state => state.Profile,
        state => ({
          user: state.user,
        }),
      ),
    [],
  );

  const { user } = useSelector(profiledropdownData);

  // Fallback string initialization to maintain avatar stability
  const [userName, setUserName] = useState(() => {
    if (user?.firstName || user?.lastName) {
      return `${user.firstName || ""} ${user.lastName || ""}`.trim();
    }
    return "Admin";
  });

  const [isProfileDropdown, setIsProfileDropdown] = useState(false);

  // Dynamic session token observer sync
  useEffect(() => {
    if (sessionStorage.getItem("authUser")) {
      const obj = JSON.parse(sessionStorage.getItem("authUser"));
      const resolvedName =
        process.env.REACT_APP_DEFAULTAUTH === "fake"
          ? obj.username === undefined
            ? user?.first_name
              ? user.first_name
              : obj.data?.first_name
            : "Admin"
          : process.env.REACT_APP_DEFAULTAUTH === "firebase"
            ? obj.email || "Admin"
            : "Admin";

      if (resolvedName) {
        setUserName(resolvedName);
      }
    }
  }, [user]);

  // Memoized Initials to prevent performance recalculations during render phases
  const initials = useMemo(() => {
    if (!userName || userName === "Admin") return "A";
    const parts = userName.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return parts[0].charAt(0).toUpperCase();
  }, [userName]);

  // Memoized Avatar dynamic coloring context based on parsed user strings
  const avatarStyle = useMemo(() => getAvatarStyle(userName), [userName]);

  // Stable toggle handler reference
  const toggleProfileDropdown = useCallback(() => {
    setIsProfileDropdown(prev => !prev);
  }, []);

  return (
    <React.Fragment>
      <Dropdown
        isOpen={isProfileDropdown}
        toggle={toggleProfileDropdown}
        className="ms-sm-2 header-item minimal-user-dropdown-container"
      >
        <DropdownToggle
          tag="button"
          type="button"
          className="btn border-0 p-0 bg-transparent shadow-none d-flex align-items-center"
        >
          <div
            className="d-flex align-items-center gap-1 user-trigger-flex-wrapper"
            style={{ padding: "4px" }}
          >
            {/* Premium Dynamic Initials Avatar Circle Trigger */}
            <div
              className="rounded-circle elite-avatar-circle d-flex align-items-center justify-content-center fw-bold text-white"
              style={{
                ...avatarStyle,
                width: "36px",
                height: "36px",
                fontSize: "13px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                transition: "transform 0.2s ease",
              }}
            >
              {initials}
            </div>

            {/* Clean Minimal Dropdown Arrow Symbol without redundant name blocks */}
            <i
              className="mdi mdi-chevron-down text-muted fs-14"
              style={{
                transition: "transform 0.2s ease",
                transform: isProfileDropdown ? "rotate(180deg)" : "rotate(0deg)",
              }}
            ></i>
          </div>
        </DropdownToggle>

        {/* Executive Quick Actions Context Menu */}
        <DropdownMenu
          className="dropdown-menu-end border-0 shadow-lg mt-2 py-2 rounded-3"
          style={{ minWidth: "160px", zIndex: 9999 }}
        >
          <h6 className="dropdown-header border-bottom text-muted fw-bold pb-2 mb-1">
            Account Actions
          </h6>
          <DropdownItem className="p-0">
            <Link
              to="/logout"
              className="dropdown-item d-flex align-items-center py-2 px-3 text-danger fw-medium"
            >
              <i className="mdi mdi-logout fs-16 align-middle me-2 opacity-75"></i>{" "}
              <span className="align-middle" data-key="t-logout">
                Logout System
              </span>
            </Link>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default React.memo(ProfileDropdown);
